<template>
  <div class="tabs-with-drawer">
    <div class="drawer-container">
      <div class="drawer-sidebar">
        <q-list style="padding: 0;">
          <slot name="drawer-header"></slot>
          <q-item-label
            v-if="!!drawerTitle"
            header
            class="text-weight-bold title"
          >
            {{ drawerTitle }}
          </q-item-label>

          <q-item
            v-for="tab in tabs"
            :key="tab.key"
            clickable
            v-ripple
            :active="activeTab === tab.key"
            @click="switchTab(tab.key)"
            active-class="text-white tw-bg-primary-light"
            class="drawer-item"
          >
            <q-item-section>
              <q-item-label>{{ tab.label }}</q-item-label>
            </q-item-section>
          </q-item>
        </q-list>
      </div>
    </div>

    <div class="content-container">
      <div class="content-header" v-if="showHeader">
        <div class="text-h6">{{ pageTitle }}</div>
      </div>

      <div class="content-body">
        <component
          v-if="currentComponent"
          :is="currentComponent"
          v-bind="currentProps"
          :key="activeTab"
        >
          <template v-if="$slots['content-header']" #header-button>
            <slot name="content-header"></slot>
          </template>
        </component>
        <div v-else class="no-content">
          <q-icon name="info" size="48px" color="grey-5" class="q-mb-md" />
          <div class="text-body1 text-grey-6">
            Выберите вкладку для отображения содержимого
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";

const props = defineProps({
  tabs: {
    type: Array,
    required: true,
    validator: (tabs) => {
      return tabs.every((tab) => tab.key && tab.label && tab.component);
    },
  },
  defaultTab: {
    type: String,
    default: null,
  },
  drawerTitle: {
    type: String,
    default: "Разделы",
  },
  pageTitle: {
    type: String,
    default: "",
  },
  showHeader: {
    type: Boolean,
    default: true,
  },
  drawerWidth: {
    type: String,
    default: "250px",
  },
});

const emit = defineEmits(["tab-changed"]);

const activeTab = ref(
  props.defaultTab || (props.tabs.length > 0 ? props.tabs[0].key : null)
);

const currentTab = computed(() => {
  return props.tabs.find((t) => t.key === activeTab.value);
});

const currentComponent = computed(() => {
  return currentTab.value ? currentTab.value.component : null;
});

const currentProps = computed(() => {
  return currentTab.value ? currentTab.value.props || {} : {};
});

function switchTab(tabKey) {
  activeTab.value = tabKey;
  emit("tab-changed", tabKey);
}

watch(
  () => props.defaultTab,
  (newDefaultTab) => {
    if (newDefaultTab) {
      activeTab.value = newDefaultTab;
    }
  }
);
</script>

<style lang="scss" scoped>
.tabs-with-drawer {
  display: flex;
  gap: 20px;
  height: 100%;
  min-height: 500px;

  .drawer-container {
    flex-shrink: 0;
    width: v-bind(drawerWidth);

    .drawer-sidebar {
      height: 100%;
      border-radius: 8px 0 0 8px;

      .q-list {
        padding: 16px 8px;
      }

      .drawer-item {
        border-radius: 8px;
      }

      .drawer-header-btn {
        margin-bottom: 12px;
        width: 100%;
      }
    }
  }

  .content-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    border-radius: 0 8px 8px 0;

    .content-header {
      padding: 16px 24px;
      border-bottom: 1px solid #e0e0e0;
    }

    .content-body {
      flex: 1;
      overflow-y: auto;

      .no-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 300px;
        text-align: center;
      }
    }
  }
}

@media (max-width: 768px) {
  .title {
    display: none;
  }
  .tabs-with-drawer {
    flex-direction: column;

    .drawer-container {
      width: 100%;

      .drawer-sidebar {
        border-radius: 8px 8px 0 0;
        border-right: none;
        border-bottom: 1px solid #e0e0e0;

        .q-list {
          display: flex;
          flex-direction: row;
          overflow-x: auto;
          padding: 8px 16px;

          .drawer-item {
            flex-shrink: 0;
            margin: 0 4px;
            min-width: 120px;
          }
        }
      }
    }

    .content-container {
      border-radius: 0 0 8px 8px;
    }
  }
}
</style>
